<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import { useConversationNotificationMute } from 'dashboard/composables/useConversationNotificationMute';
import { usePinnedConversations } from 'dashboard/composables/chatlist/usePinnedConversations';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();
const { isConversationPinned, togglePinnedConversation } =
  usePinnedConversations();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
    conversation_through_resolved: 'resolved',
    conversation_through_ai: 'ai',
    conversation_through_emails: 'email',
    conversation_through_email_inbox: 'email',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);
const contactCpf = computed(
  () => currentContact.value?.talkvia_profile?.cpf || ''
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);
const { isMuted: notificationsMuted } = useConversationNotificationMute(
  computed(() => props.chat.id)
);
const isPinned = computed(() => isConversationPinned(props.chat.id));
const pinButtonLabel = computed(() =>
  isPinned.value
    ? t('CONVERSATION.CARD_CONTEXT_MENU.UNPIN')
    : t('CONVERSATION.CARD_CONTEXT_MENU.PIN')
);
const isAiActive = computed(
  () =>
    props.chat?.meta?.assignee_type === 'AgentBot' ||
    Boolean(props.chat?.assignee_agent_bot_id)
);
const aiActiveLabel = 'IA ativa';
const separator = '•';
const hyphen = '-';
const cpfDisplay = computed(() => `CPF: ${contactCpf.value}`);
const copyCpfLabel = 'Copiar CPF';

const statusPresentation = computed(() => {
  const status = currentChat.value.status;
  const labels = {
    open: 'Em atendimento',
    pending: 'Pendente',
    resolved: 'Resolvida',
    snoozed: 'Adiada',
  };
  const tones = {
    open: 'bg-n-teal-3 text-n-teal-11 border-n-teal-5',
    pending: 'bg-n-amber-3 text-n-amber-11 border-n-amber-5',
    resolved: 'bg-n-slate-3 text-n-slate-11 border-n-slate-5',
    snoozed: 'bg-n-blue-3 text-n-blue-11 border-n-blue-5',
  };

  return {
    label: labels[status] || status,
    tone: tones[status] || tones.open,
  };
});

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};

const copyCpf = async () => {
  try {
    await copyTextToClipboard(contactCpf.value);
    useAlert('CPF copiado.', { variant: 'positive' });
  } catch (error) {
    // Clipboard unavailable.
  }
};

const togglePin = () => {
  const { limitReached } = togglePinnedConversation(props.chat.id);

  if (limitReached) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.PIN_LIMIT_REACHED'));
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="conversation-header flex min-h-0 md:min-h-24 h-auto w-full min-w-0 flex-none flex-col items-stretch justify-between gap-2 px-2 py-2 md:px-3 xl:h-12 xl:min-h-12 xl:flex-row xl:items-center"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        compact
        hide-label-on-mobile
        class="shrink-0 ltr:mr-1 md:ltr:mr-2 rtl:ml-1 md:rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        rounded-full
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div
        class="ml-2 flex min-w-0 flex-1 flex-col items-start overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div
          class="flex max-w-full min-w-0 flex-row items-center gap-1 p-0 m-0"
        >
          <span
            class="min-w-0 flex-1 truncate text-sm font-medium leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <span
            v-if="contactCpf"
            class="hidden shrink-0 items-center gap-1 sm:inline-flex"
          >
            <span class="text-sm text-n-slate-10">{{ hyphen }}</span>
            <span class="text-sm text-n-slate-11">{{ cpfDisplay }}</span>
            <button
              v-tooltip="copyCpfLabel"
              :aria-label="copyCpfLabel"
              type="button"
              class="!p-0 text-n-slate-10 hover:text-n-slate-12"
              @click="copyCpf"
            >
              <span class="i-lucide-clipboard size-3.5" />
            </button>
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex w-full flex-wrap sm:flex-nowrap items-center gap-1 text-xs conversation--header--actions text-n-slate-11 whitespace-nowrap"
        >
          <button
            type="button"
            class="shrink-0 text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span
            class="inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium"
            :class="statusPresentation.tone"
          >
            {{ statusPresentation.label }}
          </span>
          <span
            v-if="notificationsMuted"
            v-tooltip="t('CONVERSATION.NOTIFICATION_MUTE.MUTED')"
            :aria-label="t('CONVERSATION.NOTIFICATION_MUTE.MUTED')"
            class="i-hugeicons-notification-off-01 size-3.5 shrink-0 text-n-slate-11"
          />
          <span v-if="hasMultipleInboxes">{{ separator }}</span>
          <InboxName
            v-if="hasMultipleInboxes"
            :inbox="inbox"
            class="!mx-0 min-w-0 flex-1 xl:flex-none"
          />
          <span v-if="isAiActive">{{ separator }}</span>
          <span
            v-if="isAiActive"
            class="inline-flex items-center gap-1 rounded-md bg-n-teal-3 px-1.5 py-0.5 text-n-teal-11 font-medium"
          >
            <span class="i-lucide-bot size-3" />
            {{ aiActiveLabel }}
          </span>
          <span v-if="isSnoozed">{{ separator }}</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
        <div
          v-if="contactCpf"
          class="mt-0.5 flex items-center gap-1 text-[11px] text-n-slate-11 sm:hidden"
        >
          <span>{{ cpfDisplay }}</span>
          <button
            v-tooltip="copyCpfLabel"
            :aria-label="copyCpfLabel"
            type="button"
            class="!p-0 text-n-slate-10 hover:text-n-slate-12"
            @click="copyCpf"
          >
            <span class="i-lucide-clipboard size-3.5" />
          </button>
        </div>
      </div>
    </div>
    <div
      class="header-actions-wrap flex w-full flex-shrink-0 flex-row items-center justify-end gap-1 xl:w-auto xl:justify-end xl:gap-2"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <ButtonV4
        v-tooltip="pinButtonLabel"
        :aria-label="pinButtonLabel"
        :icon="isPinned ? 'i-hugeicons-pin-fill' : 'i-lucide-pin'"
        color="slate"
        :aria-pressed="isPinned"
        data-testid="conversation-header-pin"
        size="sm"
        variant="ghost"
        class="rounded-md !text-[#85858f]"
        @click="togglePin"
      />
      <SidepanelSwitch />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>

<style scoped>
.conversation-command-bar {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08), transparent 38%),
    linear-gradient(180deg, var(--n-solid-2), var(--n-solid-1));
  border-bottom: 1px solid var(--n-weak);
}

.conversation-command-bar__avatar {
  @apply rounded-xl border border-n-weak bg-n-solid-2 p-1 shadow-sm;
}

@media (max-width: 767px) {
  :deep(.button) {
    min-width: 0;
  }

  .header-actions-wrap {
    @apply gap-1;
    scrollbar-width: none;
  }

  .header-actions-wrap::-webkit-scrollbar {
    display: none;
  }

  .conversation--header--actions {
    scrollbar-width: none;
  }

  .conversation--header--actions::-webkit-scrollbar {
    display: none;
  }
}
</style>
